// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 26-versioning-update-strategy.md
// @greenlight-spec: 27-fleet-health-monitor.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { ChevronRight, Eye, EyeOff, RefreshCw } from 'lucide-react';

import { DataList, type DataListColumn } from '@/components/greenlight/DataList';
import { Dash } from '@/components/greenlight/Dash';
import { MonoValue } from '@/components/greenlight/MonoValue';
import { relativeTime } from '@/components/greenlight/relativeTime';
import { StatusBadge } from '@/components/greenlight/StatusBadge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import type { UpdateRunSummary } from '@greenlight/shared';

import { UpdaterRunDrawer } from './UpdaterRunDrawer';
import { durationLabel, modeLabel, runOutcomeMeta } from './updaterRunMeta';
import type { UpdaterRunSource } from './updaterRunSource';

/**
 * Deliberately below the endpoint's own default: this list sits inside the
 * Updater card rather than owning a page, so it has to leave room for the run
 * detail and everything below the card.
 */
const PAGE_SIZE = 8;

const COLUMNS: ReadonlyArray<DataListColumn<UpdateRunSummary>> = [
  {
    key: 'outcome',
    header: 'Outcome',
    primary: true,
    render: (run) => {
      const meta = runOutcomeMeta(run);
      return <StatusBadge variant={meta.variant}>{meta.label}</StatusBadge>;
    },
  },
  {
    key: 'target',
    header: 'Target',
    render: (run) =>
      run.target_version !== null ? <MonoValue>{run.target_version}</MonoValue> : <Dash />,
  },
  {
    key: 'how',
    header: 'How',
    priority: 'md',
    render: (run) => (
      <span className="text-[12px] text-muted-foreground">
        {modeLabel(run.mode)} · {run.trigger}
      </span>
    ),
  },
  {
    key: 'started',
    header: 'Started',
    render: (run) => <span title={run.started_at}>{relativeTime(run.started_at)}</span>,
  },
  {
    key: 'duration',
    header: 'Duration',
    priority: 'lg',
    render: (run) => (
      <span className="tabular-nums">{durationLabel(run.duration_ms) ?? 'running'}</span>
    ),
  },
];

/**
 * Every retained updater run, newest first, each opening its own detail. This
 * is the half of the Updater card that answers "what happened during run N" —
 * the panel above it answers what the install will do next.
 *
 * One page at a time with Previous/Next, rather than the accumulating
 * "load more" the full-page lists use: this list is embedded mid-card, so a
 * growing list would push the rest of Internal Health further down the page
 * with every page fetched. The card holds its height and the reader walks
 * pages, which is also how `/audit` and `/users` read a long history.
 *
 * The default view hides the checks that found nothing to apply: on a nightly
 * schedule those are almost every row, and a history where the runs that
 * changed something sit thirty rows down is one nobody opens twice. The
 * narrowing is the endpoint's rather than a pass over the fetched page, so a
 * filtered page is still a full page instead of the blanks a client-side filter
 * would leave behind.
 */
export function UpdaterRunsList({
  source,
  openRunId: controlledRunId,
  onOpenRunChange,
}: {
  readonly source: UpdaterRunSource;
  /** The run whose drawer is open, when the host keeps it (in its URL, say). */
  readonly openRunId?: string | null;
  readonly onOpenRunChange?: (runId: string | null) => void;
}): React.JSX.Element {
  const [cursor, setCursor] = React.useState<string | undefined>(undefined);
  // Cursors of the pages walked to get here, so Previous is a step back rather
  // than a fresh walk — a keyset cursor addresses forward only.
  const [cursorStack, setCursorStack] = React.useState<(string | undefined)[]>([]);
  const [ownRunId, setOwnRunId] = React.useState<string | null>(null);
  const openRunId = controlledRunId === undefined ? ownRunId : controlledRunId;
  const setOpenRunId = (runId: string | null): void => {
    if (controlledRunId === undefined) setOwnRunId(runId);
    onOpenRunChange?.(runId);
  };
  const [showUpToDate, setShowUpToDate] = React.useState(false);

  const query = source.useRuns({ limit: PAGE_SIZE, includeUpToDate: showUpToDate, cursor });
  const { items, nextCursor, hasError } = query;
  const running = items.some((run) => run.outcome === null);
  const pageNumber = cursorStack.length + 1;
  const onFirstPage = cursorStack.length === 0;

  const goNext = (): void => {
    if (nextCursor === null) return;
    setCursorStack((stack) => [...stack, cursor]);
    setCursor(nextCursor);
  };

  const goPrev = (): void => {
    if (onFirstPage) return;
    setCursor(cursorStack[cursorStack.length - 1]);
    setCursorStack((stack) => stack.slice(0, -1));
  };

  const toFirstPage = (): void => {
    setCursorStack([]);
    setCursor(undefined);
  };

  const toggleUpToDate = (): void => {
    setShowUpToDate((shown) => !shown);
    // The filter changes which runs exist, so a cursor collected under the old
    // one no longer addresses the same page.
    toFirstPage();
  };

  const toolbar = (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
      <h3 className="text-sm font-semibold text-foreground">Run history</h3>
      {running && (
        <StatusBadge variant="deploying" data-testid="updater-runs-live">
          Run in progress
        </StatusBadge>
      )}
      <div className="ml-auto flex flex-wrap items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          className="h-11 shrink-0 gap-1.5 text-muted-foreground md:h-8"
          aria-pressed={showUpToDate}
          data-testid="updater-runs-show-up-to-date"
          onClick={toggleUpToDate}
        >
          {showUpToDate ? (
            <EyeOff aria-hidden="true" className="size-4" />
          ) : (
            <Eye aria-hidden="true" className="size-4" />
          )}
          {showUpToDate ? 'Hide up-to-date checks' : 'Show up-to-date checks'}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-11 shrink-0 gap-1.5 text-muted-foreground md:h-8"
          disabled={query.isFetching}
          data-testid="updater-runs-refresh"
          onClick={() => {
            // The head page, not the page on screen. This control exists for the
            // operator who has just acted on the cluster, and their run lands at
            // the top — and only the newest page can gain a run, which is why a
            // walked-back page holds the slow cadence above. Re-reading a page
            // walked back to could not surface the thing the control is for.
            if (onFirstPage) {
              query.refetch();
              return;
            }
            toFirstPage();
            // Returning to the head alone could serve it from cache, which is
            // the interval the operator is trying not to wait out.
            query.invalidateHead();
          }}
        >
          <RefreshCw
            aria-hidden="true"
            className={query.isFetching ? 'size-4 animate-spin' : 'size-4'}
          />
          {query.isFetching ? 'Refreshing…' : 'Refresh'}
        </Button>
      </div>
    </div>
  );

  if (query.isLoading) {
    return (
      <div className="space-y-2" data-testid="updater-runs-loading">
        {toolbar}
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="space-y-2">
        {toolbar}
        <Alert variant="destructive">
          <AlertDescription>
            Couldn&rsquo;t load the run history. Retry in a moment.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="updater-runs">
      {toolbar}

      <DataList
        items={items}
        columns={[...COLUMNS]}
        rowKey={(run) => run.id}
        ariaLabel="Updater runs"
        onRowActivate={(run) => setOpenRunId(run.id)}
        rowAction={(run) => (
          <button
            type="button"
            aria-label={`Open the run that targeted ${run.target_version ?? 'no release'}`}
            className="inline-flex size-11 items-center justify-center rounded-[6px] text-muted-foreground hover:bg-muted hover:text-foreground md:size-8"
            onClick={() => setOpenRunId(run.id)}
          >
            <ChevronRight aria-hidden="true" className="size-4" />
          </button>
        )}
        empty={
          <p className="text-sm text-muted-foreground">
            {showUpToDate
              ? 'No updater runs are stored yet. The first scheduled run records one.'
              : 'No run has applied or attempted a release. Show the up-to-date checks to see the runs that found nothing to do.'}
          </p>
        }
      />

      {/* Hidden on a single page of history, so a new install is not offered
          controls that can only be inert. */}
      {(!onFirstPage || nextCursor !== null) && (
        <div
          className="flex flex-wrap items-center gap-2 pt-1"
          data-testid="updater-runs-pagination"
        >
          <span className="text-[12px] text-muted-foreground">
            Page {pageNumber} · {items.length} {items.length === 1 ? 'run' : 'runs'}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={goPrev}
              disabled={onFirstPage}
              data-testid="updater-runs-prev"
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={goNext}
              disabled={nextCursor === null}
              data-testid="updater-runs-next"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <UpdaterRunDrawer source={source} runId={openRunId} onClose={() => setOpenRunId(null)} />
    </div>
  );
}
